import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Send } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

interface ChatInputProps {
  onSend: (message: string) => void
  disabled?: boolean
  isLoading?: boolean
}

/**
 * Fixed 3-line chat input with Ctrl+Enter to send
 * Design reference: Pencil Node ID hCzZ9
 */
export function ChatInput({ onSend, disabled = false, isLoading = false }: ChatInputProps) {
  const { t } = useTranslation()
  const [message, setMessage] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const wasLoadingRef = useRef(false)

  // Auto-focus when NicolAI finishes responding (isLoading: true → false)
  useEffect(() => {
    if (wasLoadingRef.current && !isLoading && !disabled) {
      textareaRef.current?.focus()
    }
    wasLoadingRef.current = isLoading
  }, [isLoading, disabled])

  const handleSend = useCallback(() => {
    const trimmed = message.trim()
    if (!trimmed || disabled || isLoading) return

    onSend(trimmed)
    setMessage('')
  }, [message, disabled, isLoading, onSend])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Ctrl+Enter or Cmd+Enter to send
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault()
      handleSend()
    }
  }

  const canSend = message.trim().length > 0 && !disabled && !isLoading

  return (
    <div className="border-t border-border bg-background px-4 py-3 sm:px-8 sm:py-4">
      <div className="mx-auto max-w-[900px]">
        <div className="relative rounded-xl border border-border bg-background p-3 transition-colors focus-within:border-accent sm:p-4">
          {/* Textarea - fixed 3 lines, no auto-resize */}
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('scopingV2.chat.inputPlaceholder')}
            disabled={disabled || isLoading}
            className={cn(
              'h-[calc(3*1.5*14px)] w-full resize-none bg-transparent text-sm leading-normal text-foreground placeholder:text-foreground-muted',
              'focus:outline-none',
              'pr-14',
              disabled && 'cursor-not-allowed opacity-50'
            )}
            rows={3}
          />

          {/* Send button - positioned bottom right */}
          <button
            onClick={handleSend}
            disabled={!canSend}
            className={cn(
              'absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-lg transition-all sm:bottom-4 sm:right-4 sm:h-10 sm:w-10',
              canSend
                ? 'bg-accent text-white hover:bg-accent-hover'
                : 'bg-accent/50 text-white/70 cursor-not-allowed'
            )}
            aria-label={t('exchange.sendMessage')}
          >
            {isLoading ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
